import { Mail, Phone } from "lucide-react";
import { useGetOne, useTranslate, type Identifier } from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";

import { CustomFieldsAside, FieldRow } from "../customFields/CustomFields";
import { AsideSection } from "../misc/AsideSection";
import { ToDefine } from "../misc/ToDefine";
import type { Partner, Sale } from "../types";
import { PartnerSendEmailButton } from "../mail/RecordEmailWidgets";
import { PartnerKind } from "./PartnerKind";

const LinkedAccount = ({ salesId }: { salesId: Identifier }) => {
  const { data: sale } = useGetOne<Sale>("sales", { id: salesId });
  return sale ? <>{`${sale.first_name} ${sale.last_name}`}</> : null;
};

/** Contact details, notes and custom fields of a partner. */
export const PartnerDetails = ({ record }: { record: Partner }) => {
  const translate = useTranslate();
  const label = (field: string) =>
    translate(`resources.partners.fields.${field}`);

  return (
    <>
      <AsideSection title={translate("resources.partners.details")}>
        <FieldRow label={label("category")}>
          <PartnerKind partner={record} />
        </FieldRow>
        <FieldRow label={label("phone")}>
          {record.phone ? (
            <a
              href={`tel:${record.phone}`}
              className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
            >
              <Phone className="size-3.5 text-muted-foreground" />
              {record.phone}
            </a>
          ) : (
            <ToDefine />
          )}
        </FieldRow>
        <FieldRow label={label("email")}>
          {record.email ? (
            <a
              href={`mailto:${record.email}`}
              className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline break-all"
            >
              <Mail className="size-3.5 text-muted-foreground shrink-0" />
              {record.email}
            </a>
          ) : (
            <ToDefine />
          )}
        </FieldRow>
        <FieldRow label={label("organisation")}>
          {record.organisation || <ToDefine />}
        </FieldRow>
        {record.category === "staff" && record.linked_sales_id != null ? (
          <FieldRow label={label("linked_sales_id")}>
            <LinkedAccount salesId={record.linked_sales_id} />
          </FieldRow>
        ) : null}
        {record.background ? (
          <FieldRow label={label("background")}>
            <span className="block whitespace-pre-line">
              {record.background}
            </span>
          </FieldRow>
        ) : null}
      </AsideSection>
      <CustomFieldsAside resource="partners" values={record.custom_fields} />
    </>
  );
};

export const PartnerAside = ({ record }: { record: Partner }) => {
  const translate = useTranslate();
  return (
    <div className="hidden sm:block w-80 min-w-80 space-y-4">
      <div className="flex flex-wrap gap-1">
        <EditButton label={translate("resources.partners.action.edit")} />
        <PartnerSendEmailButton partner={record} />
      </div>
      <PartnerDetails record={record} />
      <div className="mt-6 pt-6 border-t flex flex-col gap-2 items-start">
        <DeleteButton
          className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
          size="sm"
        />
      </div>
    </div>
  );
};
